import { motion } from "framer-motion";

const skills = [
  "HTML/CSS",
  "React",
  "Figma",
  "Blender",
  "After Effects",
  "Photoshop",
  "Illustrator",
  "3D Modeling",
  "Motion Design",
  "UI/UX",
  "SEO",
  "Google Analytics",
  "Netlify",
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FFFDF5] section-divider"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Portrait */}
        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div
            data-testid="about-portrait"
            className="brutal-card bg-[#FFD166] p-4 -rotate-1 hover:rotate-0 transition-transform duration-300"
          >
            <picture>
              <source srcSet="/about-connor.webp" type="image/webp" />
              <img
                src="/about-connor.jpg"
                alt="Connor Corcoran, web designer and founder of Corcoran Design"
                className="w-full aspect-[4/5] object-cover border-2 border-[#0A0A0A]"
                width={800}
                height={1066}
                loading="lazy"
                decoding="async"
              />
            </picture>
          </div>
        </motion.div>

        {/* Bio */}
        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <p className="font-mono text-sm uppercase tracking-widest font-bold text-[#FE4A90] mb-3">
            About Me
          </p>
          <h2
            data-testid="about-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] uppercase"
          >
            Designer,
            <br />
            <span className="text-stroke">Builder</span>
          </h2>

          <div className="mt-8 space-y-5">
            <p
              data-testid="about-bio"
              className="font-body text-base sm:text-lg text-[#525252] leading-relaxed max-w-xl"
            >
              I'm Connor Corcoran, a designer trained at the Rhode Island School of Design (RISD) with 5+ years of experience across web design, 3D visualization, and motion graphics. I grew up around web development — my dad was an early engineer at Nest and spent his career building for the web. I've been building my own sites since middle school, from clothing brands to creative projects, and I've been designing professionally ever since.
            </p>
            <p className="font-body text-base sm:text-lg text-[#525252] leading-relaxed max-w-xl">
              I build websites for local businesses that look great and actually drive customers. If your website doesn't match the quality of your work, that's the problem I solve.
            </p>
          </div>

          {/* Stats */}
          <div className="mt-10 flex flex-wrap gap-4">
            <div
              data-testid="about-stat-years"
              className="brutal-card bg-[#06D6A0] px-6 py-4 text-center"
            >
              <p className="font-heading text-3xl text-[#0A0A0A]">5+</p>
              <p className="font-mono text-xs uppercase tracking-widest font-bold text-[#0A0A0A] mt-1">
                Years
              </p>
            </div>
            <div
              data-testid="about-stat-risd"
              className="brutal-card bg-[#FE4A90] px-6 py-4 text-center"
            >
              <p className="font-heading text-3xl text-white">RISD</p>
              <p className="font-mono text-xs uppercase tracking-widest font-bold text-white mt-1">
                Trained
              </p>
            </div>
            <div
              data-testid="about-stat-projects"
              className="brutal-card bg-[#FFD166] px-6 py-4 text-center"
            >
              <p className="font-heading text-3xl text-[#0A0A0A]">20+</p>
              <p className="font-mono text-xs uppercase tracking-widest font-bold text-[#0A0A0A] mt-1">
                Projects
              </p>
            </div>
          </div>

          {/* Skills tags */}
          <div className="mt-10">
            <p className="font-mono text-sm uppercase tracking-widest font-bold text-[#0A0A0A] mb-4">
              Toolbox
            </p>
            <div
              data-testid="about-skills"
              className="flex flex-wrap gap-3"
            >
              {skills.map((skill) => (
                <span
                  key={skill}
                  className="font-mono text-xs uppercase tracking-widest font-bold border-2 border-[#0A0A0A] px-4 py-2 bg-[#FFFDF5] hover:bg-[#0A0A0A] hover:text-[#FFFDF5] transition-colors duration-150 cursor-default"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
